import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { ATTENTION_CHIP, ATTENTION_LABEL, STAGE_LABEL, STAGE_STYLE, channelOf, formatDwell, hoursSince, isPastSla, ownerTypeOf, stageOf, subjectOf } from '../orders/orderLifecycle';
import { Car, Database, Pencil, Check, X, ChevronDown, User, RefreshCw, UserCheck, ClipboardList, Loader2, Mail, Globe, MessageCircle, Webhook, Cpu, ShoppingBag, UserRound, Bot, UserPlus, MoreHorizontal, Smartphone, Plug, Hand, ShieldCheck } from 'lucide-react';
import { Message } from './types';
import { OrderResponse, orderService } from '../../../services/OrderService';
import { useTranslation } from 'react-i18next';
import { getServiceTypes } from '../../../utils/common';
import WorkflowAuditLog from '../orders/WorkflowAuditLog';
import DebugModeToggle from './DebugModeToggle';
import ClickTrailPanel from './ClickTrailPanel';
import QaCaseExport from './QaCaseExport';
import EnrichmentDebugChip from './EnrichmentDebugChip';
import VisionDebugPanel from './VisionDebugPanel';
import CaseFilesStrip from './CaseFilesStrip';

interface ChatHeaderProps {
  activeOrder: OrderResponse | null;
  messages: Message[];
  onOrderUpdated?: (order: OrderResponse) => void;
  /** When true, hides the edit button and blocks editing (org-member or shared view) */
  isReadOnly?: boolean;
  /** Display name of the order owner, shown when different from the current user */
  ownerName?: string | null;
  /** Called when the current user takes the order (shown whenever it is not already theirs) */
  onTakeOwnership?: () => Promise<void>;
  /** True when the current user is the named owner. */
  ownerIsMe?: boolean;
  /** The attention strip is rendered right under the header — skip the duplicate flag chip. */
  stripShown?: boolean;
  /** When true, show alt_model instead of modelName */
  externalEnrichment?: boolean;
  /** Phone-only slot at the right of the summary bar (the cart button lives
      here instead of floating over the conversation). */
  mobileAction?: React.ReactNode;
  /** Attach a case file (by gs:// URI, with a preview URL) to the chat composer. Omitted in read-only views. */
  onAttachCaseFile?: (gcsUri: string, previewUrl: string, name: string, mime: string) => void;
}

const ChatHeader: React.FC<ChatHeaderProps> = ({ activeOrder, messages, onOrderUpdated, isReadOnly = false, ownerName, onTakeOwnership, ownerIsMe = false, stripShown = false, externalEnrichment = false, mobileAction, onAttachCaseFile }) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editBuyer, setEditBuyer] = useState('');
  const [editServiceType, setEditServiceType] = useState('');
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const [isTakingOwnership, setIsTakingOwnership] = useState(false);
  const [showOwnershipConfirm, setShowOwnershipConfirm] = useState(false);
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [ownerChipOpen, setOwnerChipOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [vehicleOpen, setVehicleOpen] = useState(false);

  const serviceTypes = getServiceTypes(t);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);

  // Auto-refresh order state every 15 seconds when not editing
  useEffect(() => {
    if (!activeOrder?.id || isEditing) return;

    const poll = async () => {
      try {
        setIsRefreshing(true);
        const fresh = await orderService.getOrderById(activeOrder.id);
        setLastUpdatedAt(new Date());
        // Only propagate if something relevant changed
        if (
          fresh.status !== activeOrder.status ||
          fresh.stage !== activeOrder.stage ||
          fresh.attention_flag !== activeOrder.attention_flag ||
          fresh.attention_reason !== activeOrder.attention_reason ||
          fresh.owner_uid !== activeOrder.owner_uid ||
          JSON.stringify(fresh.status_notes) !== JSON.stringify(activeOrder.status_notes) ||
          fresh.vin_data?.alt_model !== activeOrder.vin_data?.alt_model ||
          JSON.stringify(fresh.extra_data ?? null) !== JSON.stringify(activeOrder.extra_data ?? null)
        ) {
          onOrderUpdated?.(fresh);
        }
      } catch {
        // silently ignore polling errors
      } finally {
        setIsRefreshing(false);
      }
    };

    // Set initial timestamp on mount
    setLastUpdatedAt(new Date());

    const interval = setInterval(poll, 15_000);
    return () => clearInterval(interval);
  }, [activeOrder?.id, activeOrder?.status, activeOrder?.status_notes, isEditing, onOrderUpdated]);

  // Lifecycle ownership (spec §3): Agent / Needs an owner / a named person.
  const ownerType = activeOrder ? ownerTypeOf(activeOrder) : 'agent';
  const ownerLabel = !activeOrder
    ? ''
    : ownerType === 'unassigned'
      ? t('orders.owner.unassigned', { defaultValue: 'Needs an owner' })
      : ownerType === 'agent'
        ? t('orders.owner.agent', { defaultValue: 'Agent' })
        : (ownerIsMe ? t('chatHeader.ownerYou', { defaultValue: 'You' }) : (activeOrder.owner_name || ownerName || activeOrder.owner_uid || '—'));
  const OwnerIcon = ownerType === 'unassigned' ? UserPlus : ownerType === 'agent' ? Bot : User;
  const ownerChipCls = ownerType === 'unassigned'
    ? 'bg-[#FEF3C7] hover:bg-[#FDE9A8] border-amber-300 text-[#8A5A00]'
    : ownerType === 'agent'
      ? 'bg-[#f6f4f0] hover:bg-[#efebe2] border-[#efebe2] text-[#6B6862]'
      : 'bg-[#f0efff] hover:bg-[#e4e3ff] border-[#6866ff]/30 text-[#6866ff]';
  const dwell = activeOrder ? formatDwell(hoursSince(activeOrder.stage_changed_at || activeOrder.updated_at), t) : '';
  const channelMeta = (() => {
    const ch = activeOrder ? channelOf(activeOrder) : 'manual';
    const m = {
      email: { icon: <Mail className="w-2.5 h-2.5" />, cls: 'bg-sky-50 text-sky-700 border-sky-200', label: 'Email' },
      whatsapp: { icon: <Smartphone className="w-2.5 h-2.5" />, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'WhatsApp' },
      api: { icon: <Plug className="w-2.5 h-2.5" />, cls: 'bg-violet-50 text-violet-700 border-violet-200', label: 'API' },
      web: { icon: <Globe className="w-2.5 h-2.5" />, cls: 'bg-indigo-50 text-indigo-700 border-indigo-200', label: 'Web' },
      insurance: { icon: <ShieldCheck className="w-2.5 h-2.5" />, cls: 'bg-teal-50 text-teal-700 border-teal-200', label: 'Insurance' },
      manual: { icon: <Hand className="w-2.5 h-2.5" />, cls: 'bg-gray-50 text-gray-600 border-gray-200', label: 'Manual' },
    }[ch];
    return { ...m, label: t(`orders.channel.${ch}`, { defaultValue: m.label }) };
  })();
  const late = activeOrder ? isPastSla(activeOrder) : false;

  // "user"/"manual" are the default sources — showing them as a badge adds
  // noise; only surface the source when it's a real channel (same rule as
  // OrdersManagement's hasMeaningfulSource).
  const hasMeaningfulSource = (source?: string | null): boolean => {
    const s = (source || '').toLowerCase();
    return !!s && s !== 'user' && s !== 'manual';
  };

  const getSourceBadge = (source?: string | null) => {
    const src = (source || 'user').toLowerCase();
    if (src.startsWith('workflow:')) {
      const name = source!.slice('workflow:'.length).trim() || 'Automation';
      return { icon: <Cpu className="w-3 h-3" />, label: name, cls: 'bg-violet-50 text-violet-700 border-violet-200' };
    }
    switch (src) {
      case 'guest':
        return { icon: <UserRound className="w-3 h-3" />, label: 'Guest Link', cls: 'bg-amber-50 text-amber-700 border-amber-200' };
      case 'whatsapp':
        return { icon: <MessageCircle className="w-3 h-3" />, label: 'WhatsApp', cls: 'bg-green-50 text-green-700 border-green-200' };
      case 'email':
        return { icon: <Mail className="w-3 h-3" />, label: 'Email', cls: 'bg-purple-50 text-purple-700 border-purple-200' };
      case 'website':
        return { icon: <Globe className="w-3 h-3" />, label: 'Website', cls: 'bg-sky-50 text-sky-700 border-sky-200' };
      case 'api':
      case 'api_search':
        return { icon: <Webhook className="w-3 h-3" />, label: 'API', cls: 'bg-blue-50 text-blue-700 border-blue-200' };
      case 'agent':
        return { icon: <Cpu className="w-3 h-3" />, label: 'Agent', cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' };
      case 'insurance_portal':
        return { icon: <ShieldCheck className="w-3 h-3" />, label: 'Insurance Portal', cls: 'bg-teal-50 text-teal-700 border-teal-200' };
      default:
        return { icon: <ShoppingBag className="w-3 h-3" />, label: source || 'Manual', cls: 'bg-gray-50 text-gray-500 border-gray-200' };
    }
  };

  const startEditing = useCallback(() => {
    setEditBuyer(activeOrder?.buyer || '');
    setEditServiceType(activeOrder?.service_type || '');
    setIsEditing(true);
  }, [activeOrder]);

  const cancelEditing = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleTakeOwnership = useCallback(async () => {
    if (!onTakeOwnership) return;
    setIsTakingOwnership(true);
    try {
      await onTakeOwnership();
    } finally {
      setIsTakingOwnership(false);
      setShowOwnershipConfirm(false);
    }
  }, [onTakeOwnership]);

  const saveEdits = useCallback(async () => {
    if (!activeOrder) return;
    setIsSaving(true);
    try {
      const updated = await orderService.updateOrder(activeOrder.id, {
        buyer: editBuyer || null,
        service_type: editServiceType || null,
      });
      onOrderUpdated?.(updated);
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to update order:', err);
    } finally {
      setIsSaving(false);
    }
  }, [activeOrder, editBuyer, editServiceType, onOrderUpdated]);

  // Extract VIN data from messages (look for the most recent bot message with VIN_DATA)
  const vinData = activeOrder?.vin_data
  // Get all VIN data fields
  const vin = activeOrder?.vin || vinData?.vin || null;
  // Plate-lookup orgs: the plate the user gave leads the header; the VIN (which
  // still drives the search) stays one hover away.
  const plate: string | null = vinData?.plate || null;
  const manufacturer = vinData?.datasetName || '';
  // When external_enrichment is on, alt_model is the authoritative value but loads async.
  // modelName is shown as a muted placeholder until alt_model arrives.
  // null  = field not present yet → show spinner (still loading)
  // ""    = field present but empty → enrichment found nothing → fall back to modelName silently
  // "xyz" = enriched value → show it
  // Preferred source is the order's externally-synced extra_data; vin_data.alt_model
  // is the backend-maintained mirror kept for older orders.
  const extraData = activeOrder?.extra_data;
  const altModel = (extraData?.alt_model ?? vinData?.alt_model) ?? null; // null means "not yet set"
  const baseModel = vinData?.modelName || '';
  const model = useMemo(
    () => externalEnrichment
      ? (altModel || baseModel)   // '' or null both fall back to baseModel
      : baseModel,
    [externalEnrichment, altModel, baseModel]
  );
  // Show spinner only when alt_model has never been set (undefined → null), not when it's explicitly "".
  // Any written extra_data means enrichment already RAN for this order —
  // stop waiting even if it carried no alt_model (defensive twin of the
  // node-side conclude stamp). An EMPTY object counts too: order creation
  // never writes {}, so its presence proves some writer ran (observed live:
  // a workflow entity_action copying the enrich node's output wrote {} on
  // no-match, bypassing the mirror).
  const enrichmentConcluded = extraData != null && typeof extraData === 'object';
  // Grace period: enrichment runs on order creation — if nothing has written
  // alt_model within 10 minutes (no sources for this vehicle, or no
  // enrich_order workflow wired at all), stop waiting and fall back silently
  // instead of spinning forever.
  const ENRICHMENT_PENDING_GRACE_MS = 10 * 60 * 1000;
  const orderAgeMs = activeOrder?.created_at ? Date.now() - new Date(activeOrder.created_at).getTime() : Infinity;
  const modelPending = externalEnrichment && altModel === null && !!baseModel
    && !enrichmentConcluded
    && orderAgeMs < ENRICHMENT_PENDING_GRACE_MS;
  const year = vinData?.modelQualifierName || '';
  const trim = vinData?.trim || '';
  const submodel = vinData?.equipmentName || '';
  const catalogUsed = activeOrder?.catalog_used || null;
  // Vehicle panel: everything the catalog + enrichment told us about this VIN.
  const facts = activeOrder?.vehicle_facts?.facts ?? [];
  const generation = facts.find(f => f.key === 'equipmentName')?.value || '';
  const extraFactCount = facts.filter(f => !['datasetName', 'modelName', 'modelQualifierName'].includes(f.key)).length
    + (activeOrder?.vehicle_facts?.enriched_model ? 1 : 0);


  return (
    <>
      {/* No order selected — slim status strip (no agent name/tagline) */}
      {!activeOrder && (
        <div className="border-b border-[#efebe2] bg-white shadow-sm flex items-center justify-end gap-2 px-4 py-2.5">
          {mobileAction && <span className="md:hidden flex-shrink-0">{mobileAction}</span>}
          <span className="flex items-center gap-1.5 flex-shrink-0">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-xs text-[#999999]">{t('chatHeader.online', { defaultValue: 'Online' })}</span>
          </span>
        </div>
      )}

      {/* Full header — only when an order is active */}
      {activeOrder && (
      <>
      {/* Mobile version - Collapsible */}
      <div className="md:hidden border-b border-[#efebe2] bg-white animate-slideDown shadow-sm">
        {/* Always-visible summary bar */}
        <div className="flex items-center">
        <button
          className="flex-1 min-w-0 flex flex-col gap-1.5 px-3 py-2 text-left"
          onClick={() => { if (!isEditing) setMobileExpanded(v => !v); }}
        >
          {/* Row 1: vehicle title + collapse chevron */}
          <div className="flex items-center gap-2 w-full min-w-0">
            <Car className="w-3.5 h-3.5 text-[#201e1a] flex-shrink-0" />
            <span className="text-xs font-semibold text-[#201e1a] truncate flex-1 min-w-0 flex items-center gap-1">
              {manufacturer}
              {manufacturer && (altModel || baseModel) ? ', ' : ''}
              {modelPending ? (
                <>
                  <span className="text-[#999999] italic truncate">{baseModel}</span>
                  <Loader2 className="w-3 h-3 text-[#6866ff] animate-spin flex-shrink-0" />
                </>
              ) : (
                model || t('chatHeader.noOrder', { defaultValue: 'No order selected' })
              )}
              {year && <span>{year}</span>}
            </span>
            <ChevronDown className={`w-3.5 h-3.5 text-[#999999] flex-shrink-0 transition-transform duration-200 ${mobileExpanded ? 'rotate-180' : ''}`} />
          </div>

          {/* Row 2: order id + owner + status — chips wrap instead of squeezing the title */}
          {activeOrder && (
            <div className="flex items-center gap-2 flex-wrap pl-[22px]">
              {activeOrder?.external_order_id && (
                <span className="text-[10px] text-[#999999] flex-shrink-0">{activeOrder.external_order_id}</span>
              )}
              {activeOrder && (
                <span
                  onClick={(e) => { e.stopPropagation(); setOwnerChipOpen(v => !v); setShowOwnershipConfirm(false); }}
                  className={`flex items-center gap-1 text-[10px] font-medium flex-shrink-0 cursor-pointer ${ownerType === 'unassigned' ? 'text-[#8A5A00]' : ownerType === 'agent' ? 'text-[#6B6862]' : 'text-[#6866ff]'}`}
                >
                  <OwnerIcon className="w-3 h-3" />
                  {ownerLabel}
                  {!!activeOrder?.status_notes?.length && (
                    <>
                      <ClipboardList className="w-2.5 h-2.5 ml-0.5" />
                      <span className="bg-[#6866ff] text-white rounded-full px-1 leading-4 text-[9px] font-bold">
                        {activeOrder.status_notes.length}
                      </span>
                    </>
                  )}
                </span>
              )}
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold border flex-shrink-0 ${activeOrder ? STAGE_STYLE[stageOf(activeOrder)] : 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                {activeOrder ? t(`orders.stage.${stageOf(activeOrder)}`, { defaultValue: STAGE_LABEL[stageOf(activeOrder)] }) : '—'}
              </span>
              {activeOrder?.attention_flag && !stripShown && (
                <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-medium border flex-shrink-0 ${ATTENTION_CHIP[activeOrder.attention_flag]}`}>
                  {t(`orders.attention.${activeOrder.attention_flag}`, { defaultValue: ATTENTION_LABEL[activeOrder.attention_flag] })}
                </span>
              )}
            </div>
          )}
        </button>
        {mobileAction && <div className="flex-shrink-0 pr-3 pl-1">{mobileAction}</div>}
        </div>

        {/* Expanded details */}
        {mobileExpanded && (
          <div className="px-3 pb-3 border-t border-[#efebe2]">
            {!isEditing ? (
              <>
                {facts.length > 0 && (
                  <dl className="pt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[11px]">
                    {facts.map(f => (
                      <React.Fragment key={f.key}>
                        <dt className="text-[#999999]">{t(`chatHeader.vehicleFact.${f.key}`, { defaultValue: f.label })}</dt>
                        <dd className="text-[#201e1a] font-medium break-words">{f.value}</dd>
                      </React.Fragment>
                    ))}
                    {activeOrder.vehicle_facts?.enriched_model && (
                      <>
                        <dt className="text-[#999999]">{t('chatHeader.vehicleFact.enriched_model', { defaultValue: 'Enriched model' })}</dt>
                        <dd className="text-[#201e1a] font-medium">{activeOrder.vehicle_facts.enriched_model}</dd>
                      </>
                    )}
                  </dl>
                )}
                <div className="flex items-center justify-between pt-2 pb-1">
                  <div className="flex items-center gap-1.5 text-[10px] text-[#999999]">
                    {plate && <span className="font-mono text-[#201e1a] mr-2">{plate}</span>}
                    <span>VIN:</span>
                    <span className="font-mono text-[#201e1a]">{vin}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {catalogUsed && (
                      <div className="group relative flex items-center justify-center w-6 h-6 rounded-full border border-[#efebe2] bg-[#f6f4f0] hover:bg-[#efebe2] transition-colors cursor-pointer flex-shrink-0">
                        <div className="relative">
                          <Database className="w-2.5 h-2.5 text-[#201e1a]" />
                          <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-[#6866ff] rounded-full border border-white animate-pulse"></div>
                        </div>
                        {/* Tooltip */}
                        <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 rounded-lg bg-[#201e1a] text-white text-[11px] font-semibold whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 shadow-lg z-10">
                          {catalogUsed}
                          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#201e1a]" />
                        </div>
                      </div>
                    )}
                    {activeOrder && !isReadOnly && (
                      <button
                        onClick={(e) => { e.stopPropagation(); startEditing(); }}
                        className="flex items-center justify-center w-6 h-6 rounded-full bg-[#f6f4f0] hover:bg-[#efebe2] text-[#201e1a] border border-[#efebe2] transition-colors"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px]">
                  {activeOrder?.service_type && (
                    <span>
                      <span className="text-[#999999]">{t('chatHeader.serviceType', { defaultValue: 'Service' })}: </span>
                      <span className="text-[#201e1a] font-medium">{serviceTypes.find(s => s.value === activeOrder.service_type)?.label || activeOrder.service_type}</span>
                    </span>
                  )}
                  {activeOrder?.buyer && (
                    <span>
                      <span className="text-[#999999]">{t('chatHeader.notes', { defaultValue: 'Notes' })}: </span>
                      <span className="text-[#201e1a] font-medium">{activeOrder.buyer}</span>
                    </span>
                  )}
                  {activeOrder?.b2b_customer && (
                    <span>
                      <span className="text-[#999999]">{t('chatHeader.customer', { defaultValue: 'Customer' })}: </span>
                      <span className="text-[#201e1a] font-medium">
                        {activeOrder.b2b_customer.name}
                        {activeOrder.b2b_customer.alias && (
                          <span className="text-[#999999] font-normal ml-1">({activeOrder.b2b_customer.alias})</span>
                        )}
                      </span>
                    </span>
                  )}
                  {hasMeaningfulSource(activeOrder?.source) && (
                    <span className="flex items-center gap-1">
                      <span className="text-[#999999]">{t('chatHeader.source', { defaultValue: 'Source' })}: </span>
                      {(() => { const b = getSourceBadge(activeOrder.source); return (
                        <span className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${b.cls}`}>
                          {b.icon}{b.label}
                        </span>
                      ); })()}
                    </span>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between pt-2 mb-2">
                  <span className="text-[10px] text-[#999999]">Edit order details</span>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={saveEdits}
                      disabled={isSaving}
                      className="flex items-center gap-1 px-3 py-1 rounded-full bg-[#6866ff] hover:bg-[#5856e0] text-white text-[10px] font-semibold transition-colors disabled:opacity-50"
                    >
                      <Check className="w-3 h-3" />
                      {isSaving ? t('chatHeader.saving', { defaultValue: 'Saving...' }) : t('chatHeader.save', { defaultValue: 'Save' })}
                    </button>
                    <button
                      onClick={cancelEditing}
                      disabled={isSaving}
                      className="flex items-center justify-center w-6 h-6 rounded-full border border-[#efebe2] bg-white hover:bg-[#f6f4f0] text-[#201e1a] transition-colors disabled:opacity-50"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] text-[#999999] font-medium">{t('chatHeader.status', { defaultValue: 'Status' })}</span>
                    {/* Stage is system-assigned and read-only (order lifecycle spec §1.4). */}
                    <span className={`inline-flex w-fit text-[10px] font-semibold rounded-full px-2 py-1 border ${activeOrder ? STAGE_STYLE[stageOf(activeOrder)] : 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                      {activeOrder ? t(`orders.stage.${stageOf(activeOrder)}`, { defaultValue: STAGE_LABEL[stageOf(activeOrder)] }) : '—'}
                    </span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] text-[#999999] font-medium">{t('chatHeader.serviceType', { defaultValue: 'Service Type' })}</span>
                    <select
                      value={editServiceType}
                      onChange={(e) => setEditServiceType(e.target.value)}
                      className="text-[10px] border border-[#6866ff] rounded-full px-2 py-1 bg-white text-[#201e1a] focus:outline-none focus:ring-1 focus:ring-[#6866ff]"
                    >
                      <option value="">--</option>
                      {serviceTypes.map((st) => (
                        <option key={st.value} value={st.value}>{st.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-0.5 col-span-2">
                    <span className="text-[10px] text-[#999999] font-medium">{t('chatHeader.notes', { defaultValue: 'Notes' })}</span>
                    <input
                      type="text"
                      value={editBuyer}
                      onChange={(e) => setEditBuyer(e.target.value)}
                      placeholder={t('chatHeader.notesPlaceholder', { defaultValue: 'Add notes...' })}
                      className="text-[10px] border border-[#6866ff] rounded-full px-3 py-1 bg-white text-[#201e1a] placeholder-[#999999] focus:outline-none focus:ring-1 focus:ring-[#6866ff] w-full"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Desktop version — three zones: identity · state · people & actions */}
      <div className="hidden md:block border-b border-[#efebe2] bg-white animate-slideDown shadow-sm">
        <div className="px-6 py-3 flex items-start gap-4">
          {/* ── Identity ── */}
          <div className="flex items-start gap-3 min-w-0 flex-1 relative">
            <button
              type="button"
              onClick={() => { setVehicleOpen(v => !v); setMoreOpen(false); setOwnerChipOpen(false); }}
              className={`flex items-center justify-center w-10 h-10 rounded-xl flex-shrink-0 mt-0.5 border transition-colors ${vehicleOpen ? 'bg-[#EDEDFF] border-[#D6D5FF]' : 'bg-[#f6f4f0] border-transparent hover:bg-[#efebe2]'}`}
              title={t('chatHeader.vehicleDetails', { defaultValue: 'Vehicle details' })}
              aria-expanded={vehicleOpen}
            >
              <Car className="w-5 h-5 text-[#201e1a]" />
              {extraFactCount > 0 && (
                <span className="sr-only">{t('chatHeader.vehicleFactsCount', { defaultValue: '{{n}} more details', n: extraFactCount })}</span>
              )}
            </button>
            {vehicleOpen && activeOrder && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setVehicleOpen(false)} />
                <div className="absolute left-0 top-full mt-2 z-50 w-[26rem] max-w-[90vw] bg-white rounded-2xl shadow-xl border border-[#efebe2] overflow-hidden">
                  <div className="px-4 py-2.5 border-b border-[#efebe2] flex items-center gap-2">
                    <Car className="w-4 h-4 text-[#201e1a]" />
                    <span className="text-sm font-semibold text-[#201e1a]">{t('chatHeader.vehicleDetails', { defaultValue: 'Vehicle details' })}</span>
                    {vin && <span className="ml-auto font-mono text-[11px] text-[#6B6862]">{vin}</span>}
                  </div>
                  {facts.length === 0 && !activeOrder.vehicle_facts?.enriched_model ? (
                    <p className="px-4 py-4 text-xs text-[#6B6862]">{t('chatHeader.vehicleNoFacts', { defaultValue: 'The catalog returned no details for this VIN yet.' })}</p>
                  ) : (
                    <dl className="px-4 py-2 grid grid-cols-[minmax(7rem,auto)_1fr] gap-x-4 gap-y-1.5 text-xs">
                      {facts.map(f => (
                        <React.Fragment key={f.key}>
                          <dt className="text-[#6B6862]">{t(`chatHeader.vehicleFact.${f.key}`, { defaultValue: f.label })}</dt>
                          <dd className="text-[#201e1a] font-medium break-words">{f.value}</dd>
                        </React.Fragment>
                      ))}
                      {activeOrder.vehicle_facts?.enriched_model && (
                        <>
                          <dt className="text-[#6B6862]">{t('chatHeader.vehicleFact.enriched_model', { defaultValue: 'Enriched model' })}</dt>
                          <dd className="text-[#201e1a] font-medium">{activeOrder.vehicle_facts.enriched_model}</dd>
                        </>
                      )}
                    </dl>
                  )}
                  {(activeOrder.vehicle_facts?.sources?.length || catalogUsed) && (
                    <div className="px-4 py-2 border-t border-[#efebe2] flex flex-wrap items-center gap-1.5 text-[11px] text-[#6B6862]">
                      {catalogUsed && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-[#efebe2] bg-[#f6f4f0] px-2 py-0.5"><Database className="w-3 h-3" />{catalogUsed}</span>
                      )}
                      {(activeOrder.vehicle_facts?.sources || []).map(src => (
                        <span key={src} className="inline-flex items-center rounded-full border border-[#D6D5FF] bg-[#EDEDFF] text-[#4B48C7] px-2 py-0.5" title={t('chatHeader.vehicleSourceHint', { defaultValue: 'Reference data attached to this vehicle — ask about it in the chat' })}>
                          {src.replace(/@v\d+$/, '').replace(/[-_]/g, ' ')}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
            <div className="min-w-0 flex-1">
              {/* Line 1: vehicle (bold) · VIN (mono) */}
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 min-w-0">
                {(manufacturer || model || year) ? (
                  <span className="text-[15px] font-semibold text-[#201e1a] leading-tight flex items-center gap-1 truncate">
                    {manufacturer}
                    {manufacturer && (altModel || baseModel) ? ' ' : ''}
                    {modelPending ? (
                      <>
                        <span className="text-[#999999] italic font-medium">{baseModel}</span>
                        <Loader2 className="w-3 h-3 text-[#6866ff] animate-spin ml-0.5 flex-shrink-0" />
                      </>
                    ) : altModel && externalEnrichment ? (
                      <span className="transition-all duration-500 ease-in-out">{altModel}</span>
                    ) : (
                      model
                    )}
                    {year && <span className="font-medium text-[#6B6862]">{year}</span>}
                  </span>
                ) : (
                  <span className="text-[15px] font-semibold text-[#201e1a] leading-tight truncate">
                    {subjectOf(activeOrder) || activeOrder.external_order_id || t('chatHeader.noVehicleYet', { defaultValue: 'Vehicle not identified yet' })}
                  </span>
                )}
                {(plate || vin) && (
                  <span className="font-mono text-xs text-[#6B6862] tracking-tight" title={plate && vin ? `VIN ${vin}` : undefined}>{plate || vin}</span>
                )}
                {generation && (
                  <span className="text-xs text-[#6B6862] truncate max-w-[24rem]" title={generation}>{generation}</span>
                )}
                {extraFactCount > 0 && (
                  <button type="button" onClick={() => { setVehicleOpen(v => !v); setMoreOpen(false); setOwnerChipOpen(false); }} className="text-[11px] text-[#4B48C7] hover:underline whitespace-nowrap">
                    {t('chatHeader.moreVehicleDetails', { defaultValue: '+{{n}} details', n: extraFactCount })}
                  </button>
                )}
              </div>
              {/* Line 2: quiet meta — order id · channel · subject · dwell · service · notes · customer */}
              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] text-[#6B6862] min-w-0">
                {activeOrder.external_order_id && (
                  <span className="font-medium text-[#201e1a]">{activeOrder.external_order_id}</span>
                )}
                <span className={`inline-flex items-center gap-1 rounded px-1 py-[1px] text-[10px] font-semibold uppercase tracking-wide border ${channelMeta.cls}`}>
                  {channelMeta.icon}{channelMeta.label}
                </span>
                {(manufacturer || model) && subjectOf(activeOrder) && (
                  <span className="truncate max-w-[22rem]" title={subjectOf(activeOrder) || undefined}>· {subjectOf(activeOrder)}</span>
                )}
                {dwell && stageOf(activeOrder) !== 'completed' && stageOf(activeOrder) !== 'cancelled' && (
                  <span className={late ? 'text-[#8A5A00] font-medium' : ''}>· {t('orders.dwell.inStageShort', { defaultValue: '{{d}} in stage', d: dwell })}</span>
                )}
                {/* Service type — editable */}
                <span className="inline-flex items-center gap-1">
                  · {t('chatHeader.serviceType', { defaultValue: 'Service Type' })}:
                  {isEditing ? (
                    <select
                      value={editServiceType}
                      onChange={(e) => setEditServiceType(e.target.value)}
                      className="text-xs border border-[#6866ff] rounded-full px-2 py-0.5 bg-white text-[#201e1a] focus:outline-none focus:ring-1 focus:ring-[#6866ff]"
                    >
                      <option value="">{t('chatHeader.selectServiceType', { defaultValue: '-- Select --' })}</option>
                      {serviceTypes.map((st) => (
                        <option key={st.value} value={st.value}>{st.label}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-[#201e1a] font-medium">
                      {serviceTypes.find((st) => st.value === activeOrder.service_type)?.label || activeOrder.service_type || '—'}
                    </span>
                  )}
                </span>
                {/* Notes — editable */}
                {(isEditing || activeOrder.buyer) && (
                  <span className="inline-flex items-center gap-1 min-w-0">
                    · {t('chatHeader.notes', { defaultValue: 'Notes' })}:
                    {isEditing ? (
                      <input
                        type="text"
                        value={editBuyer}
                        onChange={(e) => setEditBuyer(e.target.value)}
                        placeholder={t('chatHeader.notesPlaceholder', { defaultValue: 'Add notes...' })}
                        className="text-xs border border-[#6866ff] rounded-full px-2 py-0.5 bg-white text-[#201e1a] placeholder-[#999999] focus:outline-none focus:ring-1 focus:ring-[#6866ff] w-40"
                      />
                    ) : (
                      <span className="text-[#201e1a] font-medium truncate max-w-[14rem]">{activeOrder.buyer}</span>
                    )}
                  </span>
                )}
                {activeOrder.b2b_customer && (
                  <span>
                    · {t('chatHeader.customer', { defaultValue: 'Customer' })}:{' '}
                    <span className="text-[#201e1a] font-medium">{activeOrder.b2b_customer.name}</span>
                    {activeOrder.b2b_customer.alias && <span className="ml-1">({activeOrder.b2b_customer.alias})</span>}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* ── State ── */}
          <div className="flex flex-col items-center gap-1 flex-shrink-0 pt-1" title={t('chatHeader.stageHint', { defaultValue: 'Stage is set by the system' })}>
            <span className={`text-xs font-semibold rounded-full px-3 py-1 border whitespace-nowrap ${STAGE_STYLE[stageOf(activeOrder)]}`}>
              {t(`orders.stage.${stageOf(activeOrder)}`, { defaultValue: STAGE_LABEL[stageOf(activeOrder)] })}
            </span>
            {activeOrder.attention_flag && !stripShown && (
              <span className={`rounded-md px-1.5 py-0.5 text-[11px] font-medium border whitespace-nowrap ${ATTENTION_CHIP[activeOrder.attention_flag]}`}>
                {t(`orders.attention.${activeOrder.attention_flag}`, { defaultValue: ATTENTION_LABEL[activeOrder.attention_flag] })}
              </span>
            )}
          </div>

          {/* ── People & actions ── */}
          <div className="flex items-center gap-2 flex-shrink-0 pt-0.5">
            {/* Owner pill → workflow log / take-it popover */}
            <div className="relative">
              <button
                onClick={() => { setOwnerChipOpen(v => !v); setShowOwnershipConfirm(false); setMoreOpen(false); }}
                className={`flex items-center gap-1.5 pl-2.5 pr-3 py-1.5 rounded-full border transition-colors cursor-pointer ${ownerChipCls}`}
                title={t('chatHeader.ownerHint', { defaultValue: 'Who is driving this order' })}
              >
                <OwnerIcon className="w-3.5 h-3.5" />
                <span className="text-xs font-medium max-w-[10rem] truncate">{ownerLabel}</span>
                {!!activeOrder.status_notes?.length && (
                  <span className="ml-0.5 inline-flex items-center gap-0.5 text-[10px] font-bold opacity-80">
                    <ClipboardList className="w-3 h-3" />{activeOrder.status_notes.length}
                  </span>
                )}
              </button>
              {ownerChipOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => { setOwnerChipOpen(false); setShowOwnershipConfirm(false); }} />
                  <div className="absolute right-0 top-full mt-2 z-50 w-60 bg-white rounded-2xl shadow-xl border border-[#efebe2] overflow-hidden">
                    <div className="px-4 py-2.5 text-[11px] text-[#6B6862] border-b border-[#efebe2]">
                      {ownerType === 'agent'
                        ? t('chatHeader.ownerAgentHint', { defaultValue: 'The agent is driving. Notifications go to the account queue.' })
                        : ownerType === 'unassigned'
                          ? t('chatHeader.ownerUnassignedHint', { defaultValue: 'The agent handed over and nobody has picked it up.' })
                          : t('chatHeader.ownerPersonHint', { defaultValue: 'Owned by {{name}} — notifications go to them.', name: ownerLabel })}
                    </div>
                    {!!activeOrder.status_notes?.length && (
                      <button
                        onClick={() => { setOwnerChipOpen(false); setAuditLogOpen(true); }}
                        className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-[#201e1a] hover:bg-[#f6f4f0] transition-colors border-b border-[#efebe2]"
                      >
                        <ClipboardList className="w-4 h-4 text-purple-500 flex-shrink-0" />
                        <span className="flex-1 text-left font-medium">{t('orders.auditLog.title', { defaultValue: 'Workflow Log' })}</span>
                        <span className="bg-purple-100 text-purple-700 rounded-full px-2 py-0.5 text-[10px] font-bold">{activeOrder.status_notes.length}</span>
                      </button>
                    )}
                    {onTakeOwnership && (
                      showOwnershipConfirm ? (
                        <div className="px-4 py-3 flex items-center gap-2">
                          <span className="text-xs text-[#201e1a] font-medium flex-1">{t('chatHeader.takeOwnershipConfirm', { defaultValue: 'Take this order?' })}</span>
                          <button onClick={handleTakeOwnership} disabled={isTakingOwnership} className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#6866ff] hover:bg-[#5856e0] text-white text-xs font-semibold transition-colors disabled:opacity-50">
                            <Check className="w-3 h-3" />{isTakingOwnership ? '...' : t('chatHeader.confirm', { defaultValue: 'Confirm' })}
                          </button>
                          <button onClick={() => setShowOwnershipConfirm(false)} disabled={isTakingOwnership} className="flex items-center justify-center w-6 h-6 rounded-full border border-[#efebe2] hover:bg-[#f6f4f0] text-[#201e1a] transition-colors disabled:opacity-50">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => setShowOwnershipConfirm(true)} className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-[#6866ff] hover:bg-[#f0efff] transition-colors">
                          <UserCheck className="w-4 h-4 flex-shrink-0" />
                          <span className="font-medium">{t('orders.owner.takeIt', { defaultValue: 'Take it' })}</span>
                        </button>
                      )
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Take it — the handover moment, right next to the owner pill */}
            {onTakeOwnership && ownerType === 'unassigned' && (
              showOwnershipConfirm && !ownerChipOpen ? (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-[#201e1a] font-medium">{t('chatHeader.takeOwnershipConfirm', { defaultValue: 'Take this order?' })}</span>
                  <button onClick={handleTakeOwnership} disabled={isTakingOwnership} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#8A5A00] hover:bg-[#704900] text-white text-xs font-semibold transition-colors disabled:opacity-50">
                    <Check className="w-3 h-3" />{isTakingOwnership ? t('chatHeader.taking', { defaultValue: 'Taking...' }) : t('chatHeader.confirm', { defaultValue: 'Confirm' })}
                  </button>
                  <button onClick={() => setShowOwnershipConfirm(false)} disabled={isTakingOwnership} className="flex items-center justify-center w-7 h-7 rounded-full border border-[#efebe2] hover:bg-[#f6f4f0] text-[#201e1a] transition-colors disabled:opacity-50">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => { setShowOwnershipConfirm(true); setOwnerChipOpen(false); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#8A5A00] hover:bg-[#704900] text-white text-xs font-semibold transition-colors"
                  title={t('chatHeader.takeOwnershipHint', { defaultValue: 'Assign this order to yourself' })}
                >
                  <UserCheck className="w-3.5 h-3.5" />{t('orders.owner.takeIt', { defaultValue: 'Take it' })}
                </button>
              )
            )}

            {/* Edit / save */}
            {!isReadOnly && (isEditing ? (
              <>
                <button onClick={saveEdits} disabled={isSaving} className="flex items-center gap-1 px-3.5 py-2 rounded-full bg-[#6866ff] hover:bg-[#5856e0] text-white text-xs font-semibold transition-colors disabled:opacity-50" title={t('chatHeader.save', { defaultValue: 'Save' })}>
                  <Check className="w-3.5 h-3.5" />{isSaving ? t('chatHeader.saving', { defaultValue: 'Saving...' }) : t('chatHeader.save', { defaultValue: 'Save' })}
                </button>
                <button onClick={cancelEditing} disabled={isSaving} className="flex items-center justify-center w-8 h-8 rounded-full border border-[#efebe2] hover:bg-[#f6f4f0] text-[#201e1a] transition-colors disabled:opacity-50" title={t('chatHeader.cancel', { defaultValue: 'Cancel' })}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </>
            ) : (
              <button onClick={startEditing} className="flex items-center justify-center w-8 h-8 rounded-full bg-[#f6f4f0] hover:bg-[#efebe2] text-[#201e1a] transition-colors border border-[#efebe2]" title={t('chatHeader.edit', { defaultValue: 'Edit order details' })}>
                <Pencil className="w-3.5 h-3.5" />
              </button>
            ))}

            {/* Overflow: connection, catalog and admin toggles — settings, not glance info */}
            <div className="relative">
              <button
                onClick={() => { setMoreOpen(v => !v); setOwnerChipOpen(false); }}
                className="relative flex items-center justify-center w-8 h-8 rounded-full bg-[#f6f4f0] hover:bg-[#efebe2] text-[#201e1a] transition-colors border border-[#efebe2]"
                title={t('chatHeader.more', { defaultValue: 'Connection & settings' })}
                aria-expanded={moreOpen}
              >
                <MoreHorizontal className="w-4 h-4" />
                <span className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full border border-white ${isRefreshing ? 'bg-[#6866ff] animate-pulse' : 'bg-emerald-400'}`} aria-hidden="true" />
              </button>
              {moreOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMoreOpen(false)} />
                  <div className="absolute right-0 top-full mt-2 z-50 w-72 bg-white rounded-2xl shadow-xl border border-[#efebe2] overflow-hidden">
                    <div className="px-4 py-3 flex items-center gap-2 text-xs text-[#6B6862] border-b border-[#efebe2]">
                      {isRefreshing ? <RefreshCw className="w-3 h-3 text-[#6866ff] animate-spin" /> : <span className="w-2 h-2 rounded-full bg-emerald-400" />}
                      <span>{t('chatHeader.online', { defaultValue: 'Online' })}</span>
                      {lastUpdatedAt && (
                        <span className="ml-auto">{t('chatHeader.lastUpdated', { defaultValue: 'Updated' })} {lastUpdatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      )}
                    </div>
                    {catalogUsed && (
                      <div className="px-4 py-3 flex items-center gap-2 text-xs border-b border-[#efebe2]">
                        <Database className="w-3.5 h-3.5 text-[#201e1a]" />
                        <span className="text-[#6B6862]">{t('chatHeader.catalog', { defaultValue: 'Catalog' })}</span>
                        <span className="ml-auto font-semibold text-[#201e1a]">{catalogUsed}</span>
                      </div>
                    )}
                    <div className="px-4 py-3 flex flex-col items-start gap-2 empty:hidden">
                      <DebugModeToggle />
                      <ClickTrailPanel orderId={activeOrder.id} messages={messages} />
                      <VisionDebugPanel />
                      <QaCaseExport orderId={activeOrder.id} messages={messages} context={subjectOf(activeOrder) || null} />
                      {externalEnrichment && <EnrichmentDebugChip extraData={activeOrder.extra_data} orderId={activeOrder.external_order_id || activeOrder.id} />}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Case Files — visible gallery under the header for insurance-portal orders */}
      {activeOrder?.source === 'insurance_portal' && <CaseFilesStrip orderId={activeOrder.id} onAttach={onAttachCaseFile} />}

      {/* Mobile owner popover — rendered at fragment level so it overlays everything */}
      {ownerChipOpen && activeOrder && (
        <>
          <div className="fixed inset-0 z-40 md:hidden" onClick={() => { setOwnerChipOpen(false); setShowOwnershipConfirm(false); }} />
          <div className="fixed left-1/2 -translate-x-1/2 top-24 z-50 w-64 bg-white rounded-2xl shadow-xl border border-[#efebe2] overflow-hidden md:hidden">
            {/* Audit log row */}
            {!!activeOrder?.status_notes?.length && (
              <button
                onClick={() => { setOwnerChipOpen(false); setAuditLogOpen(true); }}
                className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-[#201e1a] hover:bg-[#f6f4f0] transition-colors border-b border-[#efebe2]"
              >
                <ClipboardList className="w-4 h-4 text-purple-500 flex-shrink-0" />
                <span className="flex-1 text-left font-medium">
                  {t('orders.auditLog.title', { defaultValue: 'Workflow Log' })}
                </span>
                <span className="bg-purple-100 text-purple-700 rounded-full px-2 py-0.5 text-[10px] font-bold">
                  {activeOrder.status_notes.length}
                </span>
              </button>
            )}

            {/* Take ownership row */}
            {onTakeOwnership && (
              showOwnershipConfirm ? (
                <div className="px-4 py-3 flex items-center gap-2">
                  <span className="text-xs text-[#201e1a] font-medium flex-1">
                    {t('chatHeader.takeOwnershipConfirm', { defaultValue: 'Take this order?' })}
                  </span>
                  <button
                    onClick={handleTakeOwnership}
                    disabled={isTakingOwnership}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#6866ff] text-white text-xs font-semibold disabled:opacity-50"
                  >
                    <Check className="w-3 h-3" />
                    {isTakingOwnership ? '...' : t('chatHeader.confirm', { defaultValue: 'Confirm' })}
                  </button>
                  <button
                    onClick={() => setShowOwnershipConfirm(false)}
                    disabled={isTakingOwnership}
                    className="flex items-center justify-center w-6 h-6 rounded-full border border-[#efebe2] text-[#201e1a] disabled:opacity-50"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowOwnershipConfirm(true)}
                  className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-[#6866ff] hover:bg-[#f0efff] transition-colors"
                >
                  <UserCheck className="w-4 h-4 flex-shrink-0" />
                  <span className="font-medium">
                    {t('orders.owner.takeIt', { defaultValue: 'Take it' })}
                  </span>
                </button>
              )
            )}
          </div>
        </>
      )}

      {/* Audit log modal — shared between mobile and desktop */}
      {auditLogOpen && activeOrder?.status_notes && (
        <WorkflowAuditLog
          notes={activeOrder.status_notes}
          open={auditLogOpen}
          onClose={() => setAuditLogOpen(false)}
        />
      )}

      </>
      )}
    </>
  );
};

export default ChatHeader;
